import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
    Button,
    Col,
    DatePicker,
    Flex,
    Form,
    Input,
    Popconfirm,
    Row,
    Select,
    Space,
    Table,
    Typography,
    Upload,
} from 'antd';
import dayjs from 'dayjs';
import { Activity, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { ReactComponent as Excel } from '../../../assets/svg/excel.svg';
import {
    addPromotion,
    addPromotionProducts,
    getPromotion,
    postPromotion,
    updatePromotion,
} from '../../../redux/actions/promotions';
import { getRoutes } from '../../../routes/Routes';
import { errorToast, showToast } from '../../../utils/toast';
import TemplateModal from '../Template';
import { promotionTypes } from '../../../constants';

const { Title } = Typography;
const { Dragger } = Upload;

const AddPromotion = () => {
    const navigate = useNavigate();
    const { promotionId } = useParams();
    const [form] = Form.useForm();
    const [promotion, setPromotion] = useState(null);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [openTemplate, setOpenTemplate] = useState(false);
    const type = Form.useWatch('type', form);

    const disabledStartDate = (current) => {
        const endDate = form.getFieldValue('end_date');
        if (!endDate) return false;
        return current && current.isAfter(dayjs(endDate), 'day');
    };

    const disabledEndDate = (current) => {
        const startDate = form.getFieldValue('start_date');
        if (!startDate) return false;
        return current && current.isBefore(dayjs(startDate), 'day');
    };

    const handleFieldChange = (key, field, value, type) => {
        if (type === 'product') {
            setProducts((prev) =>
                prev.map((item) =>
                    (item.key && item.key === key) || (item.id && item.id === key)
                        ? { ...item, [field]: value }
                        : item
                )
            );
        } else if (type === 'customer') {
            setCustomers((prev) =>
                prev.map((item) =>
                    (item.key && item.key === key) || (item.id && item.id === key)
                        ? { ...item, [field]: value }
                        : item
                )
            );
        }
    };

    const handleAddRow = (type) => {
        if (type === 'product') {
            const newRow = {
                key: crypto.randomUUID(),
                product_code: '',
                price: '',
            };
            setProducts((prev) => [...prev, newRow]);
            return;
        } else if (type === 'customer') {
            const newRow = {
                key: crypto.randomUUID(),
                customer_code: '',
            };
            setCustomers((prev) => [...prev, newRow]);
            return;
        }
    };

    const handleRemoveRow = (key, type) => {
        if (type === 'product') {
            setProducts((prev) =>
                prev.filter((item) => (item.key ?? item.id) !== key)
            );
        } else if (type === 'customer') {
            setCustomers((prev) =>
                prev.filter((item) => (item.key ?? item.id) !== key)
            );
        }
    };

    const handleUpload = (file, actionType) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(e.target.result, { type: 'binary' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const excelData = XLSX.utils.sheet_to_json(sheet);

                if (!excelData.length) {
                    errorToast('Excel file is empty.');
                    return;
                }

                let requiredKeys = [];
                if (actionType === 'product') {
                    requiredKeys = ['product_code', 'price'];
                } else if (actionType === 'customer') {
                    requiredKeys =
                        type === 4 ? ['customer_code', 'product_code', 'price'] : ['customer_code'];
                }

                const fileKeys = Object.keys(excelData[0]).map((k) => k.trim().toLowerCase());
                const missingKeys = requiredKeys.filter((key) => !fileKeys.includes(key));

                if (missingKeys.length > 0) {
                    errorToast(`Missing required columns in Excel: ${missingKeys.join(', ')}`);
                    return;
                }

                const formatted = excelData.map((row) => {
                    if (actionType === 'product') {
                        return {
                            key: crypto.randomUUID(),
                            product_code: row.product_code?.toString().trim() || '',
                            price: row.price?.toString().trim() || '',
                        };
                    } else if (actionType === 'customer') {
                        return {
                            key: crypto.randomUUID(),
                            customer_code: row.customer_code?.toString().trim() || '',
                            product_code: row.product_code?.toString().trim() || '',
                            price: row.price?.toString().trim() || '',
                        };
                    }
                    return {};
                });

                const uniqueKeyFn =
                    actionType === 'product' ? (item) => item.product_code : (item) => item.customer_code;

                const unique = Array.from(
                    new Map(formatted.map((item) => [uniqueKeyFn(item), item])).values()
                );

                if (actionType === 'product') {
                    setProducts((prev) => {
                        const combined = [...prev, ...unique];
                        const finalUnique = Array.from(
                            new Map(combined.map((item) => [item.product_code, item])).values()
                        );
                        return finalUnique;
                    });
                } else if (actionType === 'customer') {
                    setCustomers((prev) => {
                        const combined = [...prev, ...unique];
                        const finalUnique = Array.from(
                            new Map(combined.map((item) => [item.customer_code, item])).values()
                        );
                        return finalUnique;
                    });
                }

                showToast(`${actionType === 'product' ? 'Products' : 'Customers'} imported successfully`);
            } catch {
                errorToast('Invalid Excel file format');
            }
        };
        reader.readAsBinaryString(file);
        return false;
    };

    const handleSubmit = async (values, type) => {
        const hasEmptyProduct = products.some((row) => !row.product_code || !row.price);
        const hasEmptyCustomer = customers.some((row) => !row.customer_code);

        if (hasEmptyProduct || hasEmptyCustomer) {
            errorToast('Please fill all rows in products and customers before submitting.');
            return;
        }

        const seen = new Set();
        const allCombinations = customers.flatMap((c) =>
            products.map((p) => ({
                customer_code: c.customer_code,
                product_code: p.product_code,
                price: p.price,
            }))
        );
        const duplicates = allCombinations.filter((row) => {
            const key = `${row.customer_code}-${row.product_code}`;
            if (seen.has(key)) return true;
            seen.add(key);
            return false;
        });

        if (duplicates.length > 0) {
            errorToast('Duplicate entries found. Please remove them before submitting.');
            return;
        }

        const cleanedCustomers = customers.map(({ key, ...rest }) => rest);
        const cleanedProducts = products.map(({ key, ...rest }) => rest);

        const body = {
            ...values,
            start_date: values.start_date.format('YYYY-MM-DD HH:mm:ss'),
            end_date: values.end_date.format('YYYY-MM-DD HH:mm:ss'),
        };

        if (promotionId) body.id = promotionId;

        const res = promotionId ? await updatePromotion(body) : await addPromotion(body);
        if (res?.ack) {
            const resp = await addPromotionProducts({
                id: promotionId || res?.record_id,
                promotions_products: cleanedProducts,
                promotions_customers: values.type === 4 ? [] : cleanedCustomers,
                promotions_customers_products:
                    values.type === 4 ? customers.map(({ key, ...rest }) => rest) : [],
            });
            if (resp?.ack) {
                navigate(getRoutes().promotion_details.route(promotionId || res?.record_id));
            } else {
                const apiErrors = resp?.errors
                if (Array.isArray(apiErrors) && apiErrors.length > 0) {
                    const errorMessages = Array.from(new Set(apiErrors));
                    errorToast(errorMessages.join('\n'));
                }

            }
        }
    };

    const handlePost = async () => {
        const res = await postPromotion({ id: promotionId });
        if (res?.ack) {
            navigate(getRoutes().promotions.route());
        }
    };

    const productColumns = [
        {
            title: 'Product Code',
            dataIndex: 'product_code',
            render: (text, record) => (
                <Input
                    value={text}
                    onChange={(e) =>
                        handleFieldChange(record.key ?? record.id, 'product_code', e.target.value, 'product')
                    }
                    placeholder="Enter product code"
                />
            ),
        },
        {
            title: 'Price',
            dataIndex: 'price',
            render: (text, record) => (
                <Input
                    value={text}
                    type="number"
                    onChange={(e) =>
                        handleFieldChange(record.key ?? record.id, 'price', e.target.value, 'product')
                    }
                    placeholder="Enter price"
                />
            ),
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title="Remove this row?"
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => handleRemoveRow(record.key ?? record.id, 'product')}
                >
                    <Button
                        color="danger"
                        variant="filled"
                        icon={<DeleteOutlined />}
                        aria-label="delete-row"
                    />
                </Popconfirm>
            ),
        },
    ];
    const customersColumns = [
        {
            title: 'Customer Code',
            dataIndex: 'customer_code',
            render: (text, record) => (
                <Input
                    value={text}
                    onChange={(e) =>
                        handleFieldChange(record.key ?? record.id, 'customer_code', e.target.value, 'customer')
                    }
                    placeholder="Enter customer code"
                />
            ),
        },
        {
            title: 'Product Code',
            dataIndex: 'product_code',
            render: (text, record) => (
                <Input
                    value={text}
                    onChange={(e) =>
                        handleFieldChange(record.key ?? record.id, 'product_code', e.target.value, 'customer')
                    }
                    placeholder="Enter product code"
                />
            ),
            hidden: type !== 4,
        },
        {
            title: 'Price',
            dataIndex: 'price',
            render: (text, record) => (
                <Input
                    value={text}
                    type="number"
                    onChange={(e) =>
                        handleFieldChange(record.key ?? record.id, 'price', e.target.value, 'customer')
                    }
                    placeholder="Enter price"
                />
            ),
            hidden: type !== 4,
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title="Remove this row?"
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => handleRemoveRow(record.key ?? record.id, 'customer')}
                >
                    <Button
                        color="danger"
                        variant="filled"
                        icon={<DeleteOutlined />}
                        aria-label="delete-row"
                    />
                </Popconfirm>
            ),
        },
    ];

    useEffect(() => {
        if (promotionId) {
            getPromotion(promotionId).then((res) => {
                if (res?.ack) {
                    setProducts(res?.promotions_products || []);
                    setPromotion(res?.promotion || null);
                    res?.promotion?.type === 4
                        ? setCustomers(res?.promotions_customers_products || [])
                        : setCustomers(res?.promotions_customers || []);
                    const promo = res?.promotion || {};
                    form.setFieldsValue({
                        title: promo?.title,
                        start_date: dayjs(promo?.start_date),
                        end_date: dayjs(promo?.end_date),
                        type: promo?.type,
                    });
                }
            });
        }
    }, [promotionId]);

    return (
        <div className="add-promotion-page">
            <Flex justify="space-between" align="center">
                <Title level={3}>{promotionId ? 'Update' : 'Add'} Promotion</Title>
                {!promotion?.posted_by && (
                    <Button
                        type="default"
                        variant="outlined"
                        color="primary"
                        onClick={() => setOpenTemplate(true)}
                    >
                        View Template
                    </Button>
                )}
            </Flex>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ type: 1 }}
                disabled={!!promotion?.posted_by}
                onValuesChange={({ type }) => {
                    if (type === 4) setProducts([]);
                }}
            >
                <Row gutter={[8, 8]}>
                    <Col sm={24} md={12} lg={6} className="w-full">
                        <Form.Item
                            name="title"
                            label="Promotion Title"
                            rules={[{ required: true, message: 'Please enter promotion title' }]}
                        >
                            <Input placeholder="Enter promotion title" />
                        </Form.Item>
                    </Col>

                    <Col sm={24} md={12} lg={6} className="w-full">
                        <Form.Item
                            name="start_date"
                            label="Start Date"
                            rules={[{ required: true, message: 'Please select start date' }]}
                            className="flex-1"
                        >
                            <DatePicker format='DD-MM-YYYY HH:mm' showTime style={{ width: '100%' }} disabledDate={disabledStartDate} />
                        </Form.Item>
                    </Col>

                    <Col sm={24} md={12} lg={6} className="w-full">
                        <Form.Item
                            name="end_date"
                            label="End Date"
                            rules={[{ required: true, message: 'Please select end date' }]}
                            className="flex-1"
                        >
                            <DatePicker format='DD-MM-YYYY HH:mm' showTime style={{ width: '100%' }} disabledDate={disabledEndDate} />
                        </Form.Item>
                    </Col>

                    <Col sm={24} md={12} lg={6} className="w-full">
                        <Form.Item
                            className="flex-1"
                            label="Type"
                            name="type"
                            rules={[{ required: true, message: 'Please select type' }]}
                        >
                            <Select
                                options={promotionTypes}
                                placeholder="Select Type"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} className="mt-3">
                    <Activity mode={type === 4 ? 'hidden' : 'visible'}>
                        <Col sm={24} md={type === 1 ? 24 : type === 4 ? 12 : 16}>
                            <Title level={5}>Promotion Products</Title>
                            <Dragger
                                beforeUpload={(file) => handleUpload(file, 'product')}
                                accept=".xlsx, .xls, .csv"
                                multiple={false}
                                showUploadList={false}
                                style={{ marginTop: 8, padding: 16, maxHeight: '200px' }}
                            >
                                <p className="ant-upload-drag-icon">
                                    <Excel width={50} height={50} />
                                </p>
                                <p className="ant-upload-text">Click or drag Excel file to upload promotion data</p>
                                <p className="ant-upload-hint">Accepted formats: .xls, .xlsx, .csv</p>
                            </Dragger>
                            <Table
                                size="small"
                                columns={productColumns}
                                dataSource={products}
                                rowKey={record => record?.key || record?.id}
                                pagination={false}
                                bordered
                                style={{ marginTop: 16 }}
                                footer={() => (
                                    <Button icon={<PlusOutlined />} onClick={() => handleAddRow('product')}>
                                        Add Row
                                    </Button>
                                )}
                                scroll={{ x: 'max-content', y: '500px' }}
                            />
                        </Col>
                    </Activity>
                    <Activity mode={type === 1 ? 'hidden' : 'visible'}>
                        <Col sm={24} md={type === 4 ? 24 : 8}>
                            <Title level={5}>Customers {type === 4 && 'Product'}</Title>
                            <Dragger
                                beforeUpload={(file) => handleUpload(file, 'customer')}
                                accept=".xlsx, .xls, .csv"
                                multiple={false}
                                showUploadList={false}
                                style={{ marginTop: 8, padding: 16, maxHeight: '200px' }}
                            >
                                <p className="ant-upload-drag-icon">
                                    <Excel width={50} height={50} />
                                </p>
                                <p className="ant-upload-text">Click or drag Excel file to upload promotion data</p>
                                <p className="ant-upload-hint">Accepted formats: .xls, .xlsx, .csv</p>
                            </Dragger>
                            <Table
                                size="small"
                                columns={customersColumns}
                                dataSource={customers}
                                rowKey="key"
                                pagination={false}
                                bordered
                                style={{ marginTop: 16 }}
                                footer={() => (
                                    <Button icon={<PlusOutlined />} onClick={() => handleAddRow('customer')}>
                                        Add Row
                                    </Button>
                                )}
                                scroll={{ x: 'max-content', y: '500px' }}
                            />
                        </Col>
                    </Activity>
                </Row>
            </Form>

            <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
                <Space>
                    <Button type="default" onClick={() => navigate(getRoutes().promotions.route())}>
                        Cancel
                    </Button>

                    <Activity mode={!!promotion?.posted_by ? 'hidden' : 'visible'}>
                        <Space>
                            {promotionId && (
                                <Popconfirm
                                    title="Confirm Promotion Post"
                                    description="After posting this promotion, further changes will no longer be possible. Do you want to continue?"
                                    okText="Post"
                                    cancelText="Cancel"
                                    okButtonProps={{
                                        variant: 'solid',
                                        color: 'danger',
                                        size: 'medium',
                                    }}
                                    cancelButtonProps={{
                                        variant: 'outlined',
                                        color: 'danger',
                                        size: 'medium',
                                    }}
                                    onConfirm={handlePost}
                                    classNames={{
                                        body: 'max-w-20rem',
                                    }}
                                >
                                    <Button type="default" color="danger" variant="solid">
                                        Post
                                    </Button>
                                </Popconfirm>
                            )}
                            <Button type="primary" onClick={() => form.submit()}>
                                {promotionId ? 'Update' : 'Save'}
                            </Button>
                        </Space>
                    </Activity>
                </Space>
            </Form.Item>

            <Activity mode={openTemplate ? 'visible' : 'hidden'}>
                <TemplateModal open={openTemplate} onClose={() => setOpenTemplate(false)} type={type} />
            </Activity>
        </div>
    );
};

export default AddPromotion;
